import { useMemo, useState, useEffect, ChangeEventHandler } from "react";
import { useSelector } from "react-redux";
import I18n from "i18n-js";
import { Dialog } from "@mui/material";
import { customAlphabet } from "nanoid";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import { RootState } from "app/store";
import { getEnv } from "app/configSlice";
import { userIconPrefixes } from "app/assets";
import Constants from "common/constant";
import { CloseBtn, SpinModal, FadeTransition } from "common/elements";
import { isIos, btnDelay, useScale, usePlaySound } from "common/utils";
import { CheckMark } from "features/user/assets";
import { createGuestAccount } from "features/user/api";

const Icon = ({
  icon,
  setIcon,
  url,
}: {
  icon: string;
  setIcon: (url: string) => void;
  url: string;
}) => {
  return (
    <div
      key={url}
      onClick={() => setIcon(url)}
      className="flex-col-center w-[84px] h-[84px] cursor-pointer"
    >
      {icon === url && (
        <div className="flex-col-el absolute top-[-16px] left-[-16px]">
          <CheckMark />
        </div>
      )}
      <img
        alt="user-icon"
        src={url}
        className={`w-full h-full object-contain border-solid ${
          icon === url
            ? " border-8 border-green-100/40"
            : " border-4 border-gray-80/40"
        }`}
      />
    </div>
  );
};

export const CreateGuestUser = ({
  open,
  close,
}: {
  open: boolean;
  close: () => void;
}) => {
  const PANEL_WIDTH = 950;
  const PANEL_HEIGHT = 680;
  const { scale } = useScale();
  const playSound = usePlaySound();
  const [error, setError] = useState("");
  const [icon, setIcon] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [disable, setDisable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [gender, setGender] = useState<string>("none");
  const [selectedTab, setSelectedTab] = useState<"NONE" | "BOY" | "GIRL">(
    "BOY",
  );
  const defaultAssets = useSelector((state: RootState) => state.config.assets);

  const icons = useMemo(() => {
    try {
      return userIconPrefixes
        .reduce(
          (pre: any[], cur: string) => [...pre, ...defaultAssets[cur]],
          [],
        )
        .filter((a: any) => a.fileSize > 0)
        .map((a: any) => ({
          url: `${Constants.assetHost}/${a.fileKey}`,
          category: a.category as "NONE" | "BOY" | "GIRL",
        }));
    } catch (e) {
      return [] as { url: string; category: "NONE" | "BOY" | "GIRL" }[];
    }
  }, [defaultAssets]);

  useEffect(() => {
    if (open) {
      const customNanoid = customAlphabet("1234567890", 7);
      const random = customNanoid();
      setName(
        I18n.t("MSG_USER_PROFILE_DEFAULT_NAME", {
          random: random,
        }),
      );
      const none = icons.find((i) => i.category === "NONE");
      const boy = icons.find((i) => i.category === "BOY");
      const girl = icons.find((i) => i.category === "GIRL");
      const first = none ?? boy ?? girl;
      if (first) setIcon(first.url);
    }
    setDisable(false);
  }, [open, icons]);

  const handleUserName: ChangeEventHandler<HTMLInputElement> = (e) => {
    if (e.target.value.length > 10) {
      setError(I18n.t("MSG_USER_NAME_LENGTH_LIMIT"));
    } else {
      if (error) {
        setError("");
      }
    }
    setName(e.target.value);
  };

  const handleChangeIcon = (gender: string) => (url: string) => {
    playSound();
    setGender(gender);
    setIcon(url);
  };

  const handleTabChange = (tab: "NONE" | "BOY" | "GIRL") => {
    playSound();
    setSelectedTab(tab);
  };

  const handleClick = async () => {
    if (!disable && !error) {
      playSound();
      setDisable(true);
      if (!name) {
        setError(I18n.t("MSG_USER_NAME_REQUIRED"));
        return;
      }

      setIsLoading(true);
      setError("");

      try {
        const response = await createGuestAccount({
          user: {
            icon,
            display_name: name,
            gender,
            locale: I18n.locale,
            env: getEnv(),
          },
        });
        const auth = getAuth();
        await signInWithCustomToken(auth, response.data.idToken);
      } catch (e) {
        console.error(e);
        setError(I18n.t("MSG_USER_CREATE_FAILED"));
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleClose = () => {
    btnDelay(() => {
      playSound();
      close();
    });
  };

  return (
    <Dialog
      open={open}
      maxWidth={false}
      sx={{ zIndex: 999999 }}
      TransitionComponent={FadeTransition}
      PaperProps={{
        style: {
          margin: 0,
          overflow: "visible",
          backgroundColor: "transparent",
        },
        elevation: 0,
      }}
    >
      <div className="flex-col-el flex-center absolute inset-0 bg-textcolor-black/50 w-full h-full">
        {isLoading && <SpinModal />}

        <div className="flex-col-el flex-center relative w-full h-full">
          <div
            className="flex-col-view self-center"
            style={{ width: PANEL_WIDTH * scale, height: PANEL_HEIGHT * scale }}
          >
            <div
              className="flex-col-view origin-top-left"
              style={{
                width: PANEL_WIDTH,
                height: PANEL_HEIGHT,
                transform: `scale(${scale})`,
              }}
            >
              <CloseBtn close={handleClose} />

              <div className="flex-col-el flex-center w-full h-full rounded-[8px] border-[8px] border-solid border-white bg-beige-60 p-6">
                <div className="flex-col-el justify-center items-center w-full mb-6">
                  <div
                    style={{ WebkitTextStroke: "1px #FEFDFE" }}
                    className="title-text !text-[52px] text-textcolor-black text-center leading-tight tracking-tight"
                  >
                    {I18n.t("MSG_USER_PROFILE_INPUT_TITLE")}
                  </div>
                </div>

                <div className="flex-row-el flex-center w-full bg-white/60 rounded-2xl py-6 gap-6">
                  <div className="flex-col-el justify-center items-start w-[310px]">
                    <p className="text-body text-textcolor-black text-2xl">
                      {I18n.t("MSG_USER_PROFILE_INPUT_NAME_PROMPT")}
                    </p>
                  </div>

                  <div className="flex-row-el flex-center w-[516px]">
                    <div className="flex-col-el w-[400px] relative">
                      <input
                        type="text"
                        value={name}
                        autoFocus={!isIos()}
                        onChange={handleUserName}
                        className={`input-modal text-body text-textcolor-black text-[26px] ${
                          error && "!border-danger"
                        }`}
                      />
                      {error && (
                        <div className="absolute -bottom-5 left-1">
                          <label className="text-body text-left text-sm h-5 text-danger">
                            {I18n.t("MSG_BLOCKLY_INPUT_LENGTH_LIMIT", {
                              limit: 10,
                            })}
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex-row-el items-center justify-between gap-2 w-full bg-white/60 my-6 rounded-2xl p-6">
                  <div className="flex-col-el flex-center">
                    <div className="flex-row-el">
                      <p className="text-body text-textcolor-black text-2xl w-full py-2">
                        {I18n.t("MSG_USER_PROFILE_SELECT_ICON_TITLE")}
                      </p>
                    </div>

                    <div className="flex-row-el flex-center cursor-pointer">
                      <div
                        className="flex-col-el flex-center relative w-[200px] h-[200px] bg-white rounded-full z-10 active:shadow-active-show active:scale-95 transition-all duration-300"
                        style={{
                          filter:
                            "drop-shadow(1px 1px 2px rgba(63, 74, 97, 0.4))",
                        }}
                      >
                        <div className="flex-col-center w-[85%] h-[85%] rounded-full overflow-hidden">
                          <img
                            key={icon}
                            alt="ユーザプロフィール画像"
                            src={icon}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {icons.length > 0 && (
                    <div className="flex-col-el flex-center gap-4">
                      <div className="flex-row-el flex-center gap-3 upsilon">
                        <button
                          onClick={() => handleTabChange("BOY")}
                          className={`btn ${
                            selectedTab === "BOY"
                              ? "btn-primary-outlined-active"
                              : "btn-primary-outlined"
                          }`}
                        >
                          <p className="text-body text-center text-[20px]">
                            {I18n.t("MSG_USER_PROFILE_FIELD_GENDER_MALE")}
                          </p>
                        </button>
                        <button
                          onClick={() => handleTabChange("GIRL")}
                          className={`btn ${
                            selectedTab === "GIRL"
                              ? "btn-primary-outlined-active"
                              : "btn-primary-outlined"
                          }`}
                        >
                          <p className="text-body text-center text-[20px]">
                            {I18n.t("MSG_USER_PROFILE_FIELD_GENDER_FEMALE")}
                          </p>
                        </button>
                        <button
                          onClick={() => handleTabChange("NONE")}
                          className={`btn ${
                            selectedTab === "NONE"
                              ? "btn-primary-outlined-active"
                              : "btn-primary-outlined"
                          }`}
                        >
                          <p className="text-body text-center text-[20px]">
                            {I18n.t("MSG_USER_PROFILE_FIELD_GENDER_NONE")}
                          </p>
                        </button>
                      </div>

                      <div className="grid grid-cols-5 grid-rows-2 w-fit gap-6">
                        {icons
                          .filter((i) => i.category === selectedTab)
                          .slice(0, 10)
                          .map((i) => (
                            <Icon
                              key={i.url}
                              icon={icon}
                              setIcon={handleChangeIcon(
                                selectedTab === "NONE"
                                  ? "none"
                                  : selectedTab === "BOY"
                                    ? "male"
                                    : "female",
                              )}
                              url={i.url}
                            />
                          ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex-row-el flex-center alpha">
                  <button
                    onClick={handleClick}
                    className="btn btn-primary"
                    disabled={name.length === 0 || !!error}
                  >
                    <div className="flex-row-center gap-1 mb-[3px]">
                      <span className="material-symbols-outlined !text-4xl text-white">
                        check
                      </span>
                      <p className="text-body text-white text-font-size-body1">
                        {I18n.t("MSG_CONFIRM_BTN")}
                      </p>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
